import {
  Group,
  type MantineColor,
  type MantineSize,
  Modal,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { useId } from "@mantine/hooks";
import type { FormEventHandler, ReactNode } from "react";

import classes from "./modal.module.css";

/**
 * AppModalProps drive the shared dialog shell every standard modal renders through.
 * `opened`/`onClose` are the controlled disclosure; `title` is the dialog heading
 * (sentence case, rendered through the theme's shared title treatment) with the
 * required one-line dimmed `description` beneath it and an optional `icon` accent to
 * its left. `children` is the body, and `footer` is the action row pinned below it
 * (usually a `<ModalFooter>`); a form-backed dialog passes `onSubmit` so the body and
 * that footer sit inside one `<form>`. `size` defaults to "md" — the form-dialog
 * width; a simple confirm passes "sm". The remaining pass-throughs are the few
 * behaviors the dialogs actually need: `zIndex` to lift a confirm over a full-screen
 * scrim, the `closeOn*` / `withCloseButton` toggles a shown-once secret dialog
 * disables so its value cannot be lost to a stray dismiss, and `closeLabel` for the
 * close button's accessible name. The surface is kept deliberately minimal —
 * fullscreen dialogs (the panorama and the two wizards) are NOT served here (see
 * below), so no fullScreen/padding/radius escape hatch is threaded.
 */
export interface AppModalProps {
  children: ReactNode;
  /**
   * The close button's accessible name — always the localized "Close", on every dialog in
   * the app, so a reader navigating by name meets one target wherever they are. Never a
   * different dismissal's word: a confirm whose X announced "Cancel" beside a footer
   * Cancel was two indistinguishable buttons. Required rather than resolved here so this
   * package stays free of the locale package, the same contract ThemeSwitcher and the
   * fullscreen shells keep.
   */
  closeLabel: string;
  /** Defaults to Mantine's true; a shown-once dialog sets false. */
  closeOnClickOutside?: boolean;
  /** Defaults to Mantine's true; a shown-once dialog sets false. */
  closeOnEscape?: boolean;
  /**
   * The line under the title: either a one-sentence description of the dialog, or the
   * identity of the single named resource it acts on. Required — a dialog that states
   * only a verb leaves the reader to infer what it will act on, and one that drops the
   * line between phases moves everything under it. Pass `null` to render without one,
   * which only `ConfirmModal` does: its body IS that sentence, and repeating it in the
   * header would say the same thing twice. Required-with-an-explicit-null rather than
   * optional, so leaving it out is a compile error instead of a silent omission.
   */
  description: ReactNode | null;
  /** The action row pinned below the body, usually a `<ModalFooter>`. */
  footer?: ReactNode;
  /** The glyph for the leading icon accent, framed in a light ThemeIcon. */
  icon?: ReactNode;
  /** Tints the icon accent; defaults to the brand color. The confirm gate sets "red". */
  iconColor?: MantineColor;
  /**
   * Keeps the dialog body mounted while closed (Mantine's `keepMounted`). A modal whose
   * body holds state that must survive a reopen sets it — the promo modal does, so its
   * field's threaded code is not wiped by a remount on each open.
   */
  keepMounted?: boolean;
  onClose: () => void;
  /**
   * Renders the body (children AND footer) as one `<form>` with this submit handler, so
   * a `<ModalFooter>` whose confirm omits `onConfirm` submits it. Without this the
   * footer is a sibling of the body and a form-backed dialog would have to smuggle its
   * action row into `children` to keep the submit button inside the form.
   */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  opened: boolean;
  /** Defaults to "md" (the form width); a confirm/simple dialog passes "sm". */
  size?: MantineSize | (string & {}) | number;
  /**
   * The dialog heading, and its accessible name. A STRING, not a node: it renders inside
   * the `<h2>` the dialog's `aria-labelledby` points at, where a node would both nest
   * flow content in a heading and read out as part of the announced name. Anything
   * richer belongs on `description`, which is a sibling of the heading for that reason.
   */
  title: string;
  /** Defaults to true; a shown-once reveal phase hides it. */
  withCloseButton?: boolean;
  zIndex?: number;
}

/**
 * AppModal is the one dialog shell every standard modal in the app composes: a
 * controlled Mantine Modal (centered, blurred + dimmed overlay, shared header band
 * and title weight — all inherited from the theme Modal defaults, not restated
 * here) whose header pairs an optional leading icon accent with the title and the
 * dimmed description beneath it, whose body is a consistent `Stack gap="md"`, and
 * whose footer (when supplied) is pinned below the body. Routing every dialog
 * through this shell makes the create/edit forms, the share and manage dialogs, the
 * export picker, and the confirm gate all settle the same way — the chrome lives
 * here, the caller supplies only the body and the action row.
 *
 * It is built on Mantine's COMPOSED parts rather than the `<Modal title={…}>`
 * shorthand, because the shorthand renders whatever it is handed inside the `<h2>`
 * the dialog's `aria-labelledby` points at. A header that carries an icon and a
 * description would then make both part of the dialog's announced name ("Duplicate
 * itinerary Alpine traverse 12 km 840 m") and nest flow content inside a heading.
 * Composed, the `<h2>` holds the title alone, the icon is its decorative sibling,
 * and the description becomes the dialog's `aria-describedby` target instead.
 *
 * The body Stack uses the `md` gap that the studio form panels share; a form whose
 * fields are already a Stack passes that Stack as a single child, so the field
 * rhythm stays the panels' rhythm rather than doubling. Inputs in those forms keep
 * their `mt={0} mb={0}` override (the theme gives TextInput/PasswordInput a
 * standard vertical rhythm for the standalone account forms), so a field sits on
 * the Stack gap like the rest.
 *
 * Fullscreen dialogs are deliberately NOT served here. The Street View panorama and
 * the embed and video wizards are full-height flex shells (a panorama that fills the
 * viewport; a header/main/footer column), which this Stack-based body would fight.
 * They stay raw `<Modal fullScreen>` and adopt the same overlay, title weight, and
 * close-button treatment through the theme defaults instead — so the chrome stays
 * consistent without forcing a layout that does not fit.
 */
export const AppModal = ({
  children,
  closeLabel,
  closeOnClickOutside,
  closeOnEscape,
  description,
  footer,
  icon,
  iconColor,
  keepMounted,
  onClose,
  onSubmit,
  opened,
  size = "md",
  title,
  withCloseButton = true,
  zIndex,
}: AppModalProps) => {
  // One id drives both halves of the accessibility wiring. Mantine derives the
  // heading's id as `${id}-title` and points aria-describedby at `${id}-body`, the id
  // it hands its own body element; the description claims that second id below and the
  // body moves to its own, so the dialog is named by the title alone and described by
  // the line under it. Mantine's useId (not React's) because it yields a DOM- and
  // CSS-safe token, and useModal passes an explicit id straight through.
  const modalId = useId();

  // The body Stack is composed once and wrapped in a <form> only when the dialog drives
  // a submit, so the two shapes cannot drift in gap or ordering. The form is a wrapper
  // rather than the Stack itself because Mantine's Stack is not polymorphic: rendering
  // it `component="form"` still types its submit handler against a div.
  const body = (
    <Stack gap="md">
      {children}
      {footer}
    </Stack>
  );

  return (
    <Modal.Root
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      id={modalId}
      keepMounted={keepMounted}
      onClose={onClose}
      opened={opened}
      size={size}
      zIndex={zIndex}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          {/* One flex child, so the header band's space-between still pushes the close
              button to the far edge. The icon accent sits OUTSIDE the heading: it is
              decoration, and the <h2> is the dialog's accessible name. */}
          <Group align="flex-start" className={classes.heading} gap="sm" wrap="nowrap">
            {icon !== undefined ? (
              <ThemeIcon color={iconColor} radius="md" size="lg" variant="light">
                {icon}
              </ThemeIcon>
            ) : null}
            <Stack className={classes.headingText} gap="xxs">
              {/* Text only. Anything else in here lands inside the heading and joins
                  the dialog's announced name. */}
              <Modal.Title>{title}</Modal.Title>
              {/* component="div" because a description may be a composed row (a
                  resource's activity glyph, name and measurements) and a <p> cannot
                  legally hold one. The class owns the weight so such a row reads at
                  body weight beside the 600 heading; c/size own the rest of the
                  treatment, here rather than at any call site. */}
              {description !== null ? (
                <Text
                  c="dimmed"
                  className={classes.description}
                  component="div"
                  id={`${modalId}-body`}
                  size="sm"
                >
                  {description}
                </Text>
              ) : null}
            </Stack>
          </Group>

          {withCloseButton ? <Modal.CloseButton aria-label={closeLabel} /> : null}
        </Modal.Header>

        {/* The description claimed Mantine's `-body` id above (that is what
            aria-describedby points at), so the body itself takes a distinct one.
            Setting aria-describedby on Modal.Content instead would type-check and do
            nothing: ModalBaseContent spreads incoming props BEFORE assigning the
            attribute, so the dialog's own value always wins. */}
        <Modal.Body id={description !== null ? `${modalId}-content` : undefined}>
          {onSubmit ? <form onSubmit={onSubmit}>{body}</form> : body}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
